import { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

import "./ProviderOtherAttachments.scss";
import { generateUUID, getObjectByKeys, promiseAllSettled } from "helpers/commonHelpers";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { uploadFile } from "api";
import Button from "components/atoms/Button/Button";
import FileAttachmentItem from "pages/common/FileAttachmentItem/FileAttachmentItem";

function ProviderOtherAttachmentsCustomBucket({ bucketAttachments, handleDraftSave }) {
  const intl = useIntl();
  const { setValue, watch } = useFormContext();
  const [files, setFiles] = useState({});

  const { permissions } = usePermissions();
  const {
    company_shipper_settings: { request_attachment_buckets },
  } = JSON.parse(permissions || "{}");

  const [formBucketAttachments, formOtherAttachments] = watch(["bucket_attachments", "other_attachments"]);
  const otherFilesAttachmentBucket = bucketAttachments?.find((attachment) => attachment.bucket_type === "other");
  const hasOtherFilesAttachmentBucket = Boolean(request_attachment_buckets && otherFilesAttachmentBucket);

  useEffect(() => {
    if (!otherFilesAttachmentBucket) return;
    if (formOtherAttachments?.length > 0) {
      const filteredFormOtherAttachments = formOtherAttachments
        ?.map((otherAttachment) => ({
          bucket_name: otherFilesAttachmentBucket?.bucket_name,
          id: otherFilesAttachmentBucket?.id,
          url: otherAttachment?.url,
          file: otherAttachment?.reference_number,
          filename: otherAttachment?.name,
        }))
        ?.filter(
          (otherAttachment) =>
            !formBucketAttachments?.some((bucketAttachment) => otherAttachment.filename === bucketAttachment.filename),
        );
      setFiles((prev) => ({
        ...prev,
        ...getObjectByKeys(filteredFormOtherAttachments, "file"),
      }));
      setValue("bucket_attachments", [...(formBucketAttachments ?? []), ...filteredFormOtherAttachments]);
      setValue("other_attachments", []);
    }
  }, [otherFilesAttachmentBucket, formOtherAttachments]);

  useEffect(() => {
    if (formBucketAttachments && !Object.keys(files).length) {
      setFiles((prev) => ({
        ...prev,
        ...getObjectByKeys(
          formBucketAttachments.filter((bucket) => bucket.bucket_name === "Other"),
          "file",
        ),
      }));
    }
  }, [formBucketAttachments]);

  const handleFilesSelect = async (event) => {
    event.persist();
    const filesData = {};
    const { files: selectedFiles } = event.target;

    Array.from(selectedFiles).forEach((file) => {
      const uuid = generateUUID();
      filesData[uuid] = {
        id: uuid,
        file,
        progress: 1,
        error: false,
        errorMessage: "",
        name: file.name,
        url: "",
        abortController: new AbortController(),
      };
    });
    setFiles((prev) => ({
      ...prev,
      ...filesData,
    }));

    const uploadingFiles = Object.values(filesData).map((f) => uploadFiles(f));

    // Filter out nulls
    const uploadedFiles = await promiseAllSettled(uploadingFiles.filter((nonNulls) => nonNulls));
    const sucessfullyUploadedFiles = uploadedFiles?.filter((file) => file.status === "fulfilled").map((file) => file.value);

    const formattedFiles = sucessfullyUploadedFiles?.map((file) => ({
      bucket_name: otherFilesAttachmentBucket?.bucket_name,
      file: file?.reference_number,
      filename: file?.name,
      url: file?.url,
      id: otherFilesAttachmentBucket?.id,
    }));
    setFiles((prev) => ({
      ...prev,
      ...formattedFiles.reduce((acc, currentFile) => ({ ...acc, [currentFile.file]: currentFile }), {}),
    }));

    setValue("bucket_attachments", [...(formBucketAttachments ?? []), ...formattedFiles]);
  };

  const uploadFiles = (data) => {
    if (!data.file) return;
    if (data.file.size >= 15_000_000) {
      updateFiles(data.id, {
        progress: null,
        error: true,
        errorMessage: intl.formatMessage({
          id: "new-request.attachments.attachmentFile.maxUploadSize",
          defaultMessage: "(max filesize 15mb)",
        }),
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("filename", data.name);

    return uploadFile(formData, {
      onUploadProgress: (progressEvent) => {
        updateFiles(data.id, {
          progress: Math.round((progressEvent.loaded * 100) / progressEvent.total),
        });
      },
      signal: data.abortController.signal,
    })
      .then((res) => {
        setFiles((prev) => {
          const cloned = { ...prev };
          const updatedData = {
            bucket_name: otherFilesAttachmentBucket?.bucket_name,
            file: res?.data?.reference_number,
            filename: res?.data?.name,
            url: res?.data?.url,
            id: otherFilesAttachmentBucket?.id,
            progress: 0,
          };
          delete cloned[data.id];
          return {
            ...cloned,
            [updatedData.file]: updatedData,
          };
        });
        return Promise.resolve(res.data);
      })
      .catch((error) => {
        if (error?.code !== "ERR_CANCELED") errorToast(error);
        updateFiles(data.id, { progress: 0, error: true, errorMessage: error?.response?.data?.detail || "" });
        return Promise.reject(error);
      });
  };

  const updateFiles = (id, data) => {
    setFiles((prev) => ({
      ...prev,
      ...(prev[id] && {
        [id]: {
          ...prev[id],
          ...data,
        },
      }),
    }));
  };

  const handleFileRemove = (data) => {
    setFiles((prev) => {
      const cloned = { ...prev };
      if (cloned[data.file]) {
        delete cloned[data.file];
      } else if (cloned[data.id]) {
        delete cloned[data.id];
      }
      return cloned;
    });
    if (formBucketAttachments?.length) {
      setValue(
        "bucket_attachments",
        formBucketAttachments.filter((file) => file.file !== data.file),
      );
    }
    handleDraftSave?.();
  };

  return (
    <div className="provider-other-attachments">
      {Object.values(files).map((file, index) => (
        <FileAttachmentItem
          key={`${file.id} ${index}`}
          currentFile={file}
          onRemove={() => handleFileRemove(file)}
          hasOtherFilesAttachmentBucket={hasOtherFilesAttachmentBucket}
        />
      ))}
      <Button variant="primary" className="rounded-pill blue-200 h-26 align-self-center position-relative">
        <FormattedMessage id="new-request.otherAttachment.addAttachment" defaultMessage="Add attachment" />
        <input
          type="file"
          accept="application/pdf, image/gif, image/tiff, text/csv, application/vnd.apple.pages, application/vnd.apple.keynote, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/msword, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png"
          className="attachment-input-file"
          multiple
          onChange={handleFilesSelect}
          onClick={(e) => {
            e.target.value = null;
          }}
        />
      </Button>
    </div>
  );
}

ProviderOtherAttachmentsCustomBucket.propTypes = {
  bucketAttachments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      bucket_name: PropTypes.string,
      bucket_type: PropTypes.string,
      company_id: PropTypes.string,
      id: PropTypes.number,
      is_active: PropTypes.bool,
      is_mandatory: PropTypes.bool,
    }),
  ),
  handleDraftSave: PropTypes.func,
};

ProviderOtherAttachmentsCustomBucket.defaultProps = {
  bucketAttachments: undefined,
  handleDraftSave: undefined,
};

export default ProviderOtherAttachmentsCustomBucket;
